import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

// Mismo criterio que ui/Modal para la trampa de foco.
const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

const focusableIn = (root) =>
  root ? Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => el.tabIndex !== -1) : [];

const sizeStyles = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-2xl',
  // Pantalla completa: sin tope de ancho. Para formularios grandes (p. ej.
  // editar plantilla de recolección con varias paradas) donde el panel
  // lateral angosto resulta incómodo.
  full: 'max-w-none',
};

export function Drawer({ open, onClose, title, description, children, size = 'md', footer, headerActions, bodyClassName }) {
  const { t } = useTranslation();
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      // Un control de adentro que ya usó el Escape (p. ej. react-select cerrando su menú
      // lo marca con preventDefault) no cierra el panel.
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  // Foco al abrir: el panel (anuncia el título y Tab entra al contenido), salvo que un
  // campo de adentro ya lo haya tomado (autoFocus). Al cerrar vuelve a donde estaba.
  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus();
    return () => {
      if (previouslyFocused?.isConnected && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  // Tab y Shift+Tab no salen del panel (lo de atrás queda tapado por el overlay).
  const trapFocus = useCallback((e) => {
    const panel = panelRef.current;
    // Los eventos de un portal hijo (p. ej. un Modal) también llegan acá por el árbol de
    // React: la trampa es solo para lo que está dentro del panel.
    if (e.key !== 'Tab' || e.defaultPrevented || !panel?.contains(e.target)) return;
    const focusables = focusableIn(panel);
    if (focusables.length === 0) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) return null;
  // Portal a <body> (igual que ui/Modal): dentro del árbol de contenido, el
  // `transform` retenido de .anim-fade-in-up / .content-main-container crea un
  // containing block y el fixed se ancla al contenedor scrolleado (drawer
  // "en blanco" fuera del viewport al abrirlo con la lista scrolleada).
  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'drawer-title' : undefined}
        tabIndex={-1}
        onKeyDown={trapFocus}
        className={cn(
          'fixed right-0 top-0 bottom-0 w-full bg-bg shadow-elevated flex flex-col focus:outline-none',
          // En pantalla completa el slide lateral barre todo el viewport y se
          // siente pesado: usamos fade. Los paneles laterales mantienen el slide.
          size === 'full' ? 'animate-fade-in' : 'animate-in',
          sizeStyles[size] ?? sizeStyles.md,
        )}
      >
        <div className="px-6 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            {title && (
              <h2 id="drawer-title" className="text-lg font-semibold text-fg">
                {title}
              </h2>
            )}
            {description && <p className="text-sm text-fg-muted mt-0.5">{description}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Acciones en la cabecera: para formularios grandes (size full) van
                acá para que el botón Guardar quede SIEMPRE visible, sin depender
                del footer que un form largo puede empujar fuera de pantalla. */}
            {headerActions}
            <button
              onClick={onClose}
              className="text-fg-muted hover:text-fg p-1 rounded-md focus:outline-none"
              aria-label={t("Cerrar")}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* min-h-0 es necesario para que overflow-y-auto haga scroll dentro del
            flex en vez de crecer y empujar el footer (Guardar) fuera de pantalla. */}
        {/* bodyClassName reemplaza el padding/scroll por defecto: layouts de varias
            columnas que scrollean cada una por su cuenta (p. ej. Programar ruta). */}
        <div className={cn('flex-1 min-h-0', bodyClassName ?? 'overflow-y-auto px-6 py-5')}>{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 bg-bg-muted/50">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}